import { ChevronRight, Compass } from 'lucide-react';
import {
  Badge,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@mlm/ui';
import type { MemberTutorialKey } from '@mlm/shared';
import { modeFor, type TutorialSignals } from '../../lib/tutorial/decide';
import { useTutorialStore, type TutorialMode } from '../../lib/tutorial/store';
import { useBrand } from '../../storefront/lib/hooks';

/** The Tutorial item in the sidebar and the mobile menu. */
export function TutorialNavButton({
  collapsed,
  onClick,
}: {
  collapsed?: boolean;
  onClick?: () => void;
}) {
  const setMenuOpen = useTutorialStore((s) => s.setMenuOpen);
  return (
    <button
      type="button"
      onClick={() => {
        onClick?.();
        setMenuOpen(true);
      }}
      title={collapsed ? 'Tutorial' : undefined}
      className={cn(
        'flex items-center gap-3 rounded-[9px] px-3.5 py-2.5 text-left text-sm font-medium text-muted-foreground transition-colors hover:bg-o-l hover:text-o',
        collapsed && 'justify-center px-2',
      )}
    >
      <Compass className="h-[18px] w-[18px] flex-none" strokeWidth={1.8} />
      {!collapsed && <span>Tutorial</span>}
    </button>
  );
}

/** Lets a member run either tutorial, for real if it's still ahead of them. */
export function TutorialMenuDialog({
  signals,
  kitsAvailable,
  impersonating,
  onStart,
}: {
  signals: TutorialSignals | null;
  kitsAvailable: boolean;
  impersonating: boolean;
  onStart: (key: MemberTutorialKey, mode: TutorialMode) => void;
}) {
  const brand = useBrand();
  const open = useTutorialStore((s) => s.menuOpen);
  const setOpen = useTutorialStore((s) => s.setMenuOpen);

  const rows: {
    key: MemberTutorialKey;
    title: string;
    live: string;
    practice: string;
    unavailable?: string;
  }[] = [
    {
      key: 'kit',
      title: `Buy your ${brand.kitLabel}`,
      live: `We'll guide you through picking a ${brand.kitLabel} and paying for it.`,
      practice: `Go through the screens for buying a ${brand.kitLabel}. Nothing is ordered.`,
      unavailable: kitsAvailable ? undefined : `No ${brand.kitLabelPlural} are on sale yet.`,
    },
    {
      key: 'activate',
      title: 'Create your slot',
      live: 'Use your Code and PIN to create your slot.',
      practice: 'Try it with a sample Code and PIN. No slot is created.',
    },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Tutorial</DialogTitle>
          <DialogDescription>Pick what you'd like to be shown.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {rows.map((row) => {
            const mode = signals ? modeFor(row.key, signals, impersonating) : 'practice';
            const disabled = !signals || !!row.unavailable;
            return (
              <button
                key={row.key}
                type="button"
                disabled={disabled}
                onClick={() => {
                  setOpen(false);
                  onStart(row.key, mode);
                }}
                className="flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
              >
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-medium">{row.title}</span>
                    {mode === 'practice' && !row.unavailable && (
                      <Badge variant="secondary">Practice</Badge>
                    )}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {row.unavailable ?? (mode === 'practice' ? row.practice : row.live)}
                  </span>
                </span>
                <ChevronRight className="h-4 w-4 flex-none text-muted-foreground" />
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
